import * as GC from '@grapecity/spread-sheets';
import { selectColumn, selectRow } from './sheet-selection';

export const openDropdown = (sheet, workbook) => {
  const row = sheet.getActiveRowIndex();
  const col = sheet.getActiveColumnIndex();
  const validator = sheet.getCell(row, col).validator();
  if (validator) {
    // if cell has data validator, show the list.
    const inputElement = workbook.getHost().getElementsByTagName('input')[0];
    if (inputElement) {
      inputElement.click();
    }
  }
};

/**
 * Bind action keys to the Worksheet that is passed by parameter. If no sheet is added, it will get the current active sheet from the book.
 * Current implemented bindings:
 * - Delete key
 * - F2
 * - Ctrl + A
 * @param callback A callback function that will be called when a key is pressed
 * @param workbook A GC.Spread.Sheets.Workbook that will bind the keys to
 * @param [sheet] - The current active work sheet. If none is specified,
 * it will use getActiveSheet() to try to get the current active sheet.
 */
export const mapActionKeys = (
  workbook: GC.Spread.Sheets.Workbook,
  sheet: GC.Spread.Sheets.Worksheet = null,
  callback = null,
  userRoleHasAccessToBinding: boolean,
  bindDeleteKey = true
) => {
  if (!sheet) {
    sheet = workbook.getActiveSheet();
  }

  fixCtrlShiftArrow(workbook);

  const commandManager = workbook.commandManager();

  // -------------------------------------------------------------------
  // ⌨ Delete key
  if (userRoleHasAccessToBinding) {
    if (bindDeleteKey) {
      commandManager.register('deleteCell', (e, info) => {
        if (!sheet.isEditing()) {
          // console.log('delete triggered', info);
          // Iterating all selected cells (multiple selections, rows and columns)
          let maxRows = 0;
          workbook.suspendPaint();
          const sheetName = sheet.name();

          sheet.getSelections().forEach((sel, i) => {
            if (maxRows < sel.row + sel.rowCount) {
              maxRows = sel.row + sel.rowCount;
            }
            for (let r = sel.row; r < sel.row + sel.rowCount; r++) {
              for (let c = sel.col; c < sel.col + sel.colCount; c++) {
                if (sheetName === 'Balance Sheet Structure' && c === 5) {
                  // When deleting Display Order cell, set it to 0
                  sheet.getCell(r, c).value(0);
                } else if (sheetName === 'Lookup Tables') {
                  sheet.getCell(r, c).value(null);
                } else {
                  sheet.getCell(r, c).clear(GC.Spread.Sheets.StorageType.data);
                }
              }
            }
            sheet.resumePaint();
          });

          workbook.resumePaint();
          // console.log(`👾 Delete pressed.`);
          callback();
        }
      });
      commandManager.setShortcutKey('deleteCell', GC.Spread.Commands.Key.del);
    }

    // -------------------------------------------------------------------
    // ⌨ F2 key
    commandManager.register('f2KeyEdit', (e, info) => {
      sheet.startEdit(false);
    });
    commandManager.setShortcutKey('f2KeyEdit', 113);

    // -------------------------------------------------------------------
    // ⌨ Alt+down, Alt+up shows the dropdown
    commandManager.register('showListDown', () => openDropdown(sheet, workbook));
    // commandManager.setShortcutKey('showListDown', GC.Spread.Commands.Key.down, false, false, true);
    // commandManager.register('showListUp', () => openDropdown(sheet, workbook));
    // commandManager.setShortcutKey('showListUp', GC.Spread.Commands.Key.up, false, false, true);

    commandManager.register(
      'showList',
      {
        canUndo: false,
        execute: function (book, options, isUndo) {
          console.log(arguments);
          const activeSheet = book.getActiveSheet();
          const r = activeSheet.getActiveRowIndex();
          const c = activeSheet.getActiveColumnIndex();
          const validator = activeSheet.getCell(r, c).validator();
          if (validator) {
            // if cell has data validator, show the list.

            book.getHost().getElementsByTagName('input')[0].click();
            book
              .getHost()
              .querySelectorAll('[gcuielement=gcValidationSelect]')[0]
              .addEventListener('keydown', event => {
                if (event.keyCode === 9) {
                  // TAB key
                  event.preventDefault();
                  // document.activeElement.blur();

                  let value = book.getHost().querySelectorAll('[gcuielement=gcValidationSelect]')[0].selectedOptions[0].text;
                  if (!isNaN(value)) {
                    value = +value; // convert the string to a number if it's a number
                  }

                  book
                    .getActiveSheet()
                    .setValue(book.getActiveSheet().getActiveRowIndex(), book.getActiveSheet().getActiveColumnIndex(), value);

                  book.commandManager().execute({ cmd: 'moveToNextCell', sheetName: book.getActiveSheet().name() });
                }
                return false;
              });
          }
        },
      },
      GC.Spread.Commands.Key.down,
      false,
      false,
      true,
      false
    );

    // -------------------------------------------------------------------
    // Add Insert Rows Below context menu option
    const ctxtMenuAddRowBelow = {
      text: 'Insert Rows Below',
      name: 'addRowBelowCmd',
      workArea: 'rowHeader',
      //  workArea: ['rowHeader', 'viewport'],
      command: 'addRowBelowCmd',
    };

    const menuList = workbook.contextMenu.menuData;
    menuList.find((obj, index, arr) => {
      // Update Insert to be named "Insert Above"
      if (obj.name === 'gc.spread.insertRows') {
        obj.text = 'Insert Rows Above';
        // Add both inserts to viewport area
        // arr.splice(index + 1, 0, { ...obj, workArea: 'viewport' });
        // arr.splice(index + 2, 0, ctxtMenuAddRowBelow);
        arr.splice(index + 1, 0, ctxtMenuAddRowBelow);
      }
      // Update delete to follow naming convention
      if (obj.name === 'gc.spread.deleteRows') {
        obj.text = 'Delete Rows';
      }
      return false;
    });

    // Command actions
    const addRowBelowCommand = {
      canUndo: false,
      execute: function (spread, options) {
        if (options.cmd === 'addRowBelowCmd') {
          const activesheet = spread.getActiveSheet();
          activesheet.suspendPaint();
          const selections = activesheet.getSelections();
          activesheet.addRows(selections[0].row + selections[0].rowCount, selections[0].rowCount);
          sheet.resumePaint();
        }
      },
    };
    // Register command
    commandManager.register('addRowBelowCmd', addRowBelowCommand, null, false, false, false, false);
  }

  // -------------------------------------------------------------------
  // ⌨ Escape
  commandManager.register('escapeKey', (e, info) => {
    // Unfocus spreadsheet when Sweetalert opens
    if (workbook && workbook.getActiveSheet() && !workbook.getActiveSheet().isEditing()) {
      workbook.focus(false);
    }
  });
  commandManager.setShortcutKey('escapeKey', GC.Spread.Commands.Key.esc);

  // -------------------------------------------------------------------
  // ⌨ Ctrl + A
  commandManager.register('selectAll', (e, info) => {
    if (!sheet.isEditing()) {
      sheet.setSelection(0, 0, sheet.getRowCount(), sheet.getColumnCount());
    }
  });
  commandManager.setShortcutKey('selectAll', GC.Spread.Commands.Key.a, true);

  // -------------------------------------------------------------------
  // ⌨ Ctrl + Space => Select Column
  commandManager.register('selectColumn', (e, info) => {
    // console.log('Select Column');
    if (!sheet.isEditing()) {
      selectColumn(sheet.getActiveColumnIndex() + 1, sheet);
    }
  });
  commandManager.setShortcutKey('selectColumn', GC.Spread.Commands.Key.space, true);

  // -------------------------------------------------------------------
  // ⌨ Shift + Space => Select Row
  commandManager.register('selectRow', (e, info) => {
    // console.log('Selected Row');
    if (!sheet.isEditing()) {
      selectRow(sheet.getActiveRowIndex() + 1, sheet);
    }
  });
  commandManager.setShortcutKey('selectRow', GC.Spread.Commands.Key.space, false, true);
};

/**
 * Add common event handlers for all sheets by attaching them to the sheet passed by parameter
 */
export function bindSpreadJSEventsToSheet(sheet: GC.Spread.Sheets.Worksheet) {
  // sheet.bind(GC.Spread.Sheets.Events.TopRowChanged, (e, info) => {
  //   if (ctrlShiftArrowPressed === 'up' || ctrlShiftArrowPressed === 'down') {
  //     sheet.showCell(
  //       info.oldTopRow,
  //       sheet.getViewportLeftColumn(1),
  //       GC.Spread.Sheets.VerticalPosition.top,
  //       GC.Spread.Sheets.HorizontalPosition.left
  //     );
  //   }
  // });
  // sheet.bind(GC.Spread.Sheets.Events.LeftColumnChanged, (e, info) => {
  //   if (ctrlShiftArrowPressed === 'left' || ctrlShiftArrowPressed === 'right') {
  //     sheet.showCell(
  //       sheet.getViewportTopRow(1),
  //       info.oldLeftCol,
  //       GC.Spread.Sheets.VerticalPosition.top,
  //       GC.Spread.Sheets.HorizontalPosition.left
  //     );
  //   }
  // });
}

// Provided by Grapecity: https://supportone.componentone.com/home/casedetail/391187
export function fixCtrlShiftArrow(spread) {
  const cm = spread.commandManager();
  cm.register('changeSelectionRowUp', getRowSelChangeCommand(-1), GC.Spread.Commands.Key.up, true, true, false, false);

  cm.register('changeSelectionRowDown', getRowSelChangeCommand(1), GC.Spread.Commands.Key.down, true, true, false, false);

  cm.register('changeSelectionColumnRight', getColSelChangeCommand(1), GC.Spread.Commands.Key.right, true, true, false, false);

  cm.register('changeSelectionColumnLeft', getColSelChangeCommand(-1), GC.Spread.Commands.Key.left, true, true, false, false);

  cm.register('changeSelectionCtrlRowUp', getCtrlSelChangeCommand(-1, 0), GC.Spread.Commands.Key.up, true, false, false, false);

  cm.register('changeSelectionCtrlRowDown', getCtrlSelChangeCommand(1, 0), GC.Spread.Commands.Key.down, true, false, false, false);
  cm.register('changeSelectionCtrlColLeft', getCtrlSelChangeCommand(0, -1), GC.Spread.Commands.Key.left, true, false, false, false);
  cm.register('changeSelectionCtrlColRight', getCtrlSelChangeCommand(0, 1), GC.Spread.Commands.Key.right, true, false, false, false);
}

function getCtrlSelChangeCommand(rowInc, colInc) {
  const command = {
    canUndo: false,
    execute: function (wbContext, options, isUndo) {
      const sheet = wbContext.getSheetFromName(options.sheetName);
      if (!sheet) {
        return;
      }

      const activeRow = sheet.getActiveRowIndex(),
        activeCol = sheet.getActiveColumnIndex();

      const rng = getNextSelection(sheet, activeRow, activeCol, rowInc, colInc);
      sheet.setSelection(rng.row, rng.col, rng.rowCount, rng.colCount);

      return true;
    },
  };
  return command;
}

function getNextSelection(sheet, row, col, rowInc, colInc) {
  let startRow = row + rowInc,
    startCol = col + colInc;
  const isFirstNull = sheet.getValue(startRow, startCol) == null;
  while (
    startRow < sheet.getRowCount() &&
    startCol < sheet.getColumnCount() &&
    startRow >= 0 &&
    startCol >= 0 &&
    ((isFirstNull && sheet.getValue(startRow, startCol) == null) ||
      (!isFirstNull && sheet.getValue(startRow + rowInc, startCol + colInc) != null))
  ) {
    startRow += rowInc;
    startCol += colInc;
  }

  return {
    row: startRow < 0 ? 0 : startRow, // Ensures you're no falling into the header bar
    col: startCol < 0 ? 0 : startCol, // because the algorithm provided col/row index -1
    rowCount: 1,
    colCount: 1,
  };
}

function getColSelChangeCommand(inc) {
  const command = {
    canUndo: false,
    execute: function (wbContext, options, isUndo) {
      const sheet = wbContext.getSheetFromName(options.sheetName);
      if (!sheet) {
        return;
      }
      const selections = sheet.getSelections();
      const activeRow = sheet.getActiveRowIndex(),
        activeCol = sheet.getActiveColumnIndex();

      const selNeedAdjust = getNeedAdjustSelection(selections, activeRow, activeCol);
      if (inc === 1 && selNeedAdjust.col + selNeedAdjust.colCount >= sheet.getColumnCount() - 1) {
        // executed command, no need to change selection, already at last
        return true;
      } else if (inc === -1 && selNeedAdjust.col === 0) {
        // executed command, no need to change selection, already at last
        return true;
      }

      const rng = getNextSelRangeForColumn(selNeedAdjust, sheet, inc);
      Object.assign(selNeedAdjust, rng);

      sheet.repaint();

      return true;
    },
  };
  return command;
}

function getRowSelChangeCommand(inc) {
  const command = {
    canUndo: false,
    execute: function (wbContext, options, isUndo) {
      const sheet = wbContext.getSheetFromName(options.sheetName);
      if (!sheet) {
        return;
      }
      const selections = sheet.getSelections();
      const activeRow = sheet.getActiveRowIndex(),
        activeCol = sheet.getActiveColumnIndex();

      const selNeedAdjust = getNeedAdjustSelection(selections, activeRow, activeCol);
      if (inc === 1 && selNeedAdjust.row + selNeedAdjust.rowCount >= sheet.getRowCount() - 1) {
        // executed command, no need to change selection, already at last
        return true;
      } else if (inc === -1 && selNeedAdjust.row === 0) {
        // executed command, no need to change selection, already at last
        return true;
      }
      const rng = getNextSelRangeForRow(selNeedAdjust, sheet, inc);

      Object.assign(selNeedAdjust, rng);
      sheet.repaint();

      return true;
    },
  };
  return command;
}

function getNeedAdjustSelection(selections, rowIndex, colIndex) {
  let sel = null;
  for (let i = 0; i < selections.length; i++) {
    if (selections[i].contains(rowIndex, colIndex)) {
      sel = selections[i];
    }
  }
  return sel;
}

function getNextSelRangeForColumn(rng, sheet, inc) {
  const rngClone = Object.assign({}, rng);

  const activeRow = sheet.getActiveRowIndex(),
    activeCol = sheet.getActiveColumnIndex();

  let start = rngClone.col === activeCol ? rngClone.col + rngClone.colCount - 1 : rngClone.col;

  start += inc;
  const isFirstNull = sheet.getValue(activeRow, start) == null;
  while (
    start < sheet.getColumnCount() - 1 &&
    ((isFirstNull && sheet.getValue(activeRow, start) == null) || (!isFirstNull && sheet.getValue(activeRow, start + inc) != null)) &&
    start > 0 &&
    start !== activeCol
  ) {
    start += inc;
  }

  return {
    row: rngClone.row,
    rowCount: rngClone.rowCount,
    col: Math.min(start, activeCol),
    colCount: Math.abs(start - activeCol) + 1,
  };
}

function getNextSelRangeForRow(rng, sheet, inc) {
  const rngClone = Object.assign({}, rng);

  const activeRow = sheet.getActiveRowIndex(),
    activeCol = sheet.getActiveColumnIndex();

  let start = rngClone.row === activeRow ? rngClone.row + rngClone.rowCount - 1 : rngClone.row;

  start += inc;
  const isFirstNull = sheet.getValue(start, activeCol) == null;
  while (
    start < sheet.getRowCount() - 1 &&
    ((isFirstNull && sheet.getValue(start, activeCol) == null) || (!isFirstNull && sheet.getValue(start + inc, activeCol) != null)) &&
    start > 0 &&
    start !== activeRow
  ) {
    start += inc;
  }

  return {
    row: Math.min(start, activeRow),
    rowCount: Math.abs(start - activeRow) + 1,
    col: rngClone.col,
    colCount: rngClone.colCount,
  };
}
